import React, { useContext, useEffect, lazy } from 'react';
import { Route, Routes, useLocation, Navigate, useParams } from 'react-router-dom';
import { SuspenseWrapper } from './SuspenseWrapper';
import PrivateRoute from './PrivateRoute';

import { helpKey, supportKey } from 'constants/moviDeskKeys';

import { Index } from 'pages/Index';
import { RefreshSession } from 'pages/RefreshSession';
const Login = lazy(() => import('pages/Login'));
import { Logout } from 'pages/Login/Logout';
import { ForgotPassword } from 'pages/Login/ForgotPassword';
import { ResetPassword } from 'pages/Login/ResetPassword';
const ErrorPage = lazy(() => import('pages/ErrorPage'));
const Occurrence = lazy(() => import('pages/Reports/Occurrence'));
const Pmoc = lazy(() => import('pages/Reports/Pmoc'));
const Availability = lazy(() => import('pages/Reports/Availability'));
const ViewGeneralPlansPage = lazy(() => import('pages/Activities/GeneralPlans/ViewGeneralPlansPage'));
const NewGeneralPlanPage = lazy(() => import('pages/Activities/GeneralPlans/NewGeneralPlanPage'));
const EditGeneralPlanPage = lazy(() => import('pages/Activities/GeneralPlans/EditGeneralPlanPage'));
import { AuthContext } from 'context/AuthContext';
import { PrivateGeneralPlansRoute, PrivateScheduleAdminRoute } from 'pages/Activities/GeneralPlans/utils';
const Geolocalize = lazy(() => import('pages/Geolocalize'));
const ReportsPage = lazy(() => import('pages/Reports/ReportsPage'));
const ClientScheduleMonitoring = lazy(() => import('pages/Agendamento/AgendamentoClientes'));
const AdminScheduleMonitoring = lazy(() => import('pages/Admin/AgendamentoAdmin'));
const Activities = lazy(() => import('pages/Reports/Activities'));
const QRcodes = lazy(() => import('pages/Reports/QRcodes'));
const QRcodesList = lazy(() => import('pages/Reports/QRcodesList'));

const Home = lazy(() => import('pages/Home'));
const OccurrenceGeneratedPDF = lazy(() => import('pages/Reports/Occurrence/PDFOcurrence'));
import jwt from 'jwt-decode';
import { PermissionContextProvider } from 'context/PermissionsContext';
const ConectionError = lazy(() => import('pages/ErrorPage/ConnectionError'));
const PermissionError = lazy(() => import('pages/ErrorPage/PermissionError'));
const LandingQR = lazy(() => import('pages/LandingQR'));
const PushNotification = lazy(() => import('pages/Configurations/PushNotification'));
const CompaniesRegistrationPage = lazy(() => import('pages/Registration/Companies'));
const CalendarReport = lazy(() => import('pages/Reports/Calendar'));
const CompaniesPage = lazy(() => import('pages/Companies'));
const LoginSSO = lazy(() => import('pages/Login/LoginSSO'));
const CustomerPortal = lazy(() => import('pages/CustomerPortal'));
const ChamadoSemLogin = lazy(() => import('pages/ChamadoSemLogin'));
const Agenda = lazy(() => import('pages/Agenda'));
const Documents = lazy(() => import('pages/Documents'));
const NewDocument = lazy(() => import('pages/Documents/components/Novo Documento'));
const Edit = lazy(() => import('pages/Documents/components/Edição'));
const TechnicalHours = lazy(() => import('pages/Reports/TechinicalHours'));
const MaterialsManagement = lazy(() => import('pages/Reports/MaterialsManagement'));
const UserAccess = lazy(() => import('pages/Configurations/UserAccess'));
//const NewDashBoard = lazy(() => import('pages/NewDashboard'));
const AssetsReports = lazy(() => import('pages/Reports/Assets'));
const ChamadoConfig = lazy(() => import('pages/Configurations/Chamado'));

const AberturaChamadoConfig = lazy(() => import('pages/Configurations/AberturaChamado'));
const CapacidadeOperacional = lazy(() => import('pages/Configurations/CapacidadeOperacional'));
const AutomaticConfigActivities = lazy(() => import('pages/Reports/Activities/AutomaticConfig'));
const Dashboard = lazy(() => import('pages/NewDashboard/index.jsx'));
const Solicitacoes = lazy(() => import('pages/Almoxarifado/Solicitacoes'));
const Movimentacao = lazy(() => import('pages/Almoxarifado/Movimentacao'));

const RedirectChamadoWoba = () => {
  const { qrcode } = useParams();
  return <Navigate to={`/chamado/${qrcode}`} replace />;
};

const AppRoutes = () => {
  //const { setSocketIoContext } = useContext(SocketIoContext);
  const { authContext } = useContext(AuthContext);
  const { user, token } = authContext;
  const isCommonUser = user?.permissionType === `user`;
  const addMovideskScript = () => {
    const { ussupp } = jwt(token);
    const mdClient = document.createElement('script');
    const movideskScript = document.createElement('script');
    if (user.name !== null) {
      // O widget do Movidesk reestrutura o próprio DOM depois de inicializado (cria seus
      // containers e pode mover/substituir esses nós) — removeChild direto em document.body
      // pode achar um nó que não é mais filho DELE especificamente, jogando uma DOMException
      // não tratada. parentNode.removeChild acha o pai real, seja ele qual for.
      const alreadyExistMdClient = document.getElementById('mdClient');
      if (alreadyExistMdClient?.parentNode) {
        alreadyExistMdClient.parentNode.removeChild(alreadyExistMdClient);
      }
      const alreadyExistMovideskScript = document.getElementById('movideskScript');
      if (alreadyExistMovideskScript?.parentNode) {
        alreadyExistMovideskScript.parentNode.removeChild(alreadyExistMovideskScript);
      }
      const alreadyExistMdWidget = document.getElementById('md-app-widget');
      if (alreadyExistMdWidget?.parentNode) {
        alreadyExistMdWidget.parentNode.removeChild(alreadyExistMdWidget);
      }
      const mdKey = ussupp === 'True' ? supportKey : helpKey;
      mdClient.id = 'mdClient';
      mdClient.type = 'text/javascript';
      mdClient.innerText = `var mdChatClient = '${mdKey}';`;
      mdClient.async = false;
      document.body.appendChild(mdClient);
      movideskScript.src = 'https://chat.movidesk.com/Scripts/chat-widget.min.js';
      movideskScript.async = true;
      movideskScript.id = 'movideskScript';
      document.body.appendChild(movideskScript);
    }
  };
  const location = useLocation();
  useEffect(() => {
    if (token) {
      const excludedPaths = [
        '/login',
        '/logout',
        '/home',
        '/configuracoes/acesso-de-usuario',
        '/relatorios/qr-codes-impressao',
        '/chamado',
      ];
      if (!excludedPaths.includes(location.pathname) && !location.pathname.startsWith('/chamado')) {
        addMovideskScript();
      }
    }
    // eslint-disable-next-line
  }, [token, user]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      if (
        location.pathname !== '/login' &&
        location.pathname !== '/logout' &&
        !location.pathname.startsWith('/chamado')
      ) {
        const script = document.createElement('script');
        script.src = '//tag.goadopt.io/injector.js?website_code=762d4206-3c1c-4c53-bb3a-eebe62d4fbea';
        script.className = 'adopt-injector';
        document.body.appendChild(script);

        const meta = document.createElement('meta');
        meta.name = 'adopt-website-id';
        meta.content = '762d4206-3c1c-4c53-bb3a-eebe62d4fbea';
        document.head.appendChild(meta);

        const script2 = document.createElement('script');
        script2.textContent = 'window.adoptHideAfterConsent=true;';
        document.head.appendChild(script2);

        return () => {
          // O script de terceiros (tag.goadopt.io) manipula o DOM por conta própria (insere
          // seu próprio banner, e alguns scripts assim removem/substituem a própria tag depois
          // de rodar) — removeChild sem checar o parentNode pode achar um nó que já não é mais
          // filho, jogando uma DOMException dentro do cleanup do effect (comprometendo a
          // reconciliação do React na troca de rota seguinte).
          if (script.parentNode) script.parentNode.removeChild(script);
          if (meta.parentNode) meta.parentNode.removeChild(meta);
          if (script2.parentNode) script2.parentNode.removeChild(script2);
        };
      }
    }
  }, [location.pathname]);
  return (
    <PermissionContextProvider>
      <SuspenseWrapper>
        <Routes>
          {/* Refresh session */}
          <Route path="/refresh" element={<RefreshSession />} />
          {/* Login */}
          <Route path="/login" element={<Login />} />
          <Route path="/login-sso/callback" element={<LoginSSO />} />
          {/* Misc */}
          <Route path="/" element={<Index />} />
          {/* Portal do cliente */}
          <Route
            path="/portal-do-cliente/*"
            element={<PrivateRoute path="/portal-do-cliente" Component={CustomerPortal} />}
          />
          {/* Private routes */}
          <Route path="/agenda" element={<PrivateRoute path="/agenda" page="agenda" Component={Agenda} />} />
          <Route path="/home" element={<PrivateRoute path="/home" page="home" Component={Home} />} />
          <Route
            path="/geolocalizacao"
            element={<PrivateRoute path="/geolocalizacao" page="geolocalizacao" Component={Geolocalize} />}
          />
          <Route
            path="/dashboard/*"
            element={<PrivateRoute path="/dashboard" page="dashboard" Component={Dashboard} />}
          />

          <Route
            path="/almoxarifado/solicitacoes"
            element={<PrivateRoute path="/almoxarifado/solicitacoes" page="almoxarifado" Component={Solicitacoes} />}
          />

          <Route
            path="/almoxarifado/movimentacao"
            element={<PrivateRoute path="/almoxarifado/movimentacao" page="almoxarifado" Component={Movimentacao} />}
          />

          {/* Documentos */}
          <Route
            path="/documentos"
            element={<PrivateRoute path="/documentos" page="documentos" Component={Documents} />}
          />
          <Route
            path="/documentos/novoDocumento"
            element={<PrivateRoute path="/documentos/novoDocumento" page="documentos" Component={NewDocument} />}
          />
          <Route
            path="/documentos/:id/editar"
            element={<PrivateRoute path="/documentos/:id/editar" page="documentos" Component={Edit} />}
          />
          {/* Relatórios */}
          <Route
            path="/relatorios"
            element={<PrivateRoute path="/relatorios" page="relatorios" Component={ReportsPage} />}
          />
          <Route
            path="/relatorios/ocorrencia/*"
            element={<PrivateRoute path="/relatorios/ocorrencia" page="relatorios" Component={Occurrence} />}
          />
          <Route
            path="/relatórios/relatorios-gerados-ocorrencia-pdf"
            element={
              <PrivateRoute
                path="/relatórios/relatorios-gerados-ocorrencia-pdf"
                page="relatorios"
                Component={OccurrenceGeneratedPDF}
              />
            }
          />
          <Route
            path="/relatorios/atividades/automaticos/novo"
            element={
              <PrivateRoute
                path="/relatorios/atividades/automaticos/novo"
                page="relatorios"
                Component={AutomaticConfigActivities}
              />
            }
          />
          <Route
            path="/relatorios/atividades/automaticos/:id/editar"
            element={
              <PrivateRoute
                path="/relatorios/atividades/automaticos/:id/editar"
                page="relatorios"
                Component={AutomaticConfigActivities}
              />
            }
          />
          <Route
            path="/relatorios/horas-tecnicas/*"
            element={<PrivateRoute path="/relatorios/horas-tecnicas" page="relatorios" Component={TechnicalHours} />}
          />
          <Route
            path="/relatorios/gestao-de-materiais/*"
            element={
              <PrivateRoute path="/relatorios/gestao-de-materiais" page="relatorios" Component={MaterialsManagement} />
            }
          />
          <Route
            path="/relatorios/disponibilidade/*"
            element={<PrivateRoute path="/relatorios/disponibilidade" page="relatorios" Component={Availability} />}
          />
          <Route
            path="/relatorios/pmoc/*"
            element={<PrivateRoute path="/relatorios/pmoc" page="relatorios" Component={Pmoc} />}
          />
          <Route
            path="/relatorios/atividades/*"
            element={<PrivateRoute path="/relatorios/atividades" page="relatorios" Component={Activities} />}
          />
          <Route
            path="/relatorios/qr-codes/*"
            element={<PrivateRoute path="/relatorios/qr-codes" page="relatorios" Component={QRcodes} />}
          />
          <Route
            path="/relatorios/qr-codes-impressao/*"
            element={<PrivateRoute path="/relatorios/qr-codes-impressao" page="relatorios" Component={QRcodesList} />}
          />
          <Route
            path="/relatorios/calendario/*"
            element={<PrivateRoute path="/relatorios/calendario" page="relatorios" Component={CalendarReport} />}
          />
          <Route
            path="/relatorios/ativos/*"
            element={<PrivateRoute path="/relatorios/ativos" page="relatorios" Component={AssetsReports} />}
          />
          {/* Configurações */}
          <Route
            path="/configuracoes/chamado"
            element={<PrivateRoute path="/configuracoes/chamado" page="config" Component={ChamadoConfig} />}
          />

          <Route
            path="/configuracoes/abertura-chamado"
            element={
              <PrivateRoute path="/configuracoes/abertura-chamado" page="config" Component={AberturaChamadoConfig} />
            }
          />
          <Route
            path="/configuracoes/notificacoes-push"
            element={
              <PrivateRoute path="/configuracoes/notificacoes-push" page="config" Component={PushNotification} />
            }
          />
          <Route
            path="/configuracoes/acesso-de-usuario"
            element={<PrivateRoute path="/configuracoes/acesso-de-usuario" page="cadastros" Component={UserAccess} />}
          />
          {/* Acesso só por URL direta por ora — o item de menu vem do backend, fora de escopo aqui */}
          {import.meta.env.VITE_MODE === 'homolog' && (
            <Route
              path="/configuracoes/capacidade-operacional"
              element={
                <PrivateRoute
                  path="/configuracoes/capacidade-operacional"
                  page="config"
                  Component={CapacidadeOperacional}
                />
              }
            />
          )}
          {/* Cadastros */}
          <Route
            path="/cadastros/empresas"
            element={<PrivateRoute path="/cadastros/empresas" page="cadastros" Component={CompaniesRegistrationPage} />}
          />
          {/* Atividades — Planos Gerais */}
          <Route
            path="/atividades/planos-gerais"
            element={
              <PrivateGeneralPlansRoute
                isDisabled={isCommonUser}
                path="/atividades/planos-gerais"
                page="planosGerais"
                Component={ViewGeneralPlansPage}
              />
            }
          />
          <Route
            path="/atividades/planos-gerais/novo"
            element={
              <PrivateGeneralPlansRoute
                isDisabled={isCommonUser}
                path="/atividades/planos-gerais/novo"
                page="planosGerais"
                Component={NewGeneralPlanPage}
              />
            }
          />
          <Route
            path="/atividades/planos-gerais/:id"
            element={
              <PrivateGeneralPlansRoute
                isDisabled={isCommonUser}
                path="/atividades/planos-gerais/:id"
                page="planosGerais"
                Component={EditGeneralPlanPage}
              />
            }
          />
          {/* Monitoria */}
          <Route path="/monitoria-de-agendamento-cliente" element={<ClientScheduleMonitoring />} />
          <Route
            path="/monitoria-de-agendamento-admin"
            element={
              <PrivateScheduleAdminRoute
                isDisabled={isCommonUser}
                path="/monitoria-de-agendamento-admin"
                Component={AdminScheduleMonitoring}
              />
            }
          />
          {/* Conta / Auth */}
          <Route path="/conta/recuperar" element={<ForgotPassword />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/conta/redefinir-senha/:token" element={<ResetPassword />} />
          {/* Erros */}
          <Route path="/conection-error" element={<ConectionError />} />
          <Route path="/permission-error" element={<PermissionError />} />
          {/* Misc público */}
          <Route path="/qr/:code" element={<LandingQR />} />
          <Route path="/clientes/:subdominio" element={<CompaniesPage />} />
          <Route path="/chamado-woba/:qrcode" element={<RedirectChamadoWoba />} />
          <Route path="/chamado/:qrcode" element={<ChamadoSemLogin />} />
          {/* 404 */}
          <Route path="*" element={<ErrorPage errorCode="404" />} />
        </Routes>
      </SuspenseWrapper>
    </PermissionContextProvider>
  );
};

export default AppRoutes;
